import React, { useRef, useState } from "react";
import MenuIcon from '@mui/icons-material/Menu';
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, styled } from '@mui/material';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { logDocumentDownload, logDownloadFailed } from "../../../fetch/insightsHelper";
import { useNotification, usePhoneMode } from "../../../hooks";
import useLiteratureCurrentView from "../hooks/useLiteratureCurrentView";
import { useIsAuthenticated } from "@azure/msal-react";
import FeedbackModal from "./FeedbackModal";
import ChatIcon from '@mui/icons-material/Chat';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useSelector } from "react-redux";
import { useDownloadMetadata } from "../../../fetch/fetchMetadata";
import DownloadSectionMenuItem from "./Download/DownloadSectionMenuItem";
import { getAuthenticatedHeaders } from "../../../fetch/fetchHeaders";
import Spinner from "../../spinner/Spinner";

const MessageContainer = styled('div')(({ theme, offsetLeft, offsetTop, finished }) => ({
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
    color: 'black',
    position: 'absolute',
    width: '20em',
    height: finished ? '8em' : '8em',
    top: offsetTop,
    left: offsetLeft,
}));

export default function MobileMenu({ spMode }) {
    const { partNumber } = useParams();
    const { showErrorModal } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();
    const anchorRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = !!anchorEl;

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [downloadFinished, setDownloadFinished] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showDownloadSubMenu, setShowDownloadSubMenu] = useState(false);

    const isAuthenticated = useIsAuthenticated();
    const { phoneMode } = usePhoneMode();
    const { isInFaultView, isInFaultList, isInDocumentView, isInDocumentListView, isInEngineInfo } = useLiteratureCurrentView();

    const showFeedbackMenuItem = phoneMode && isAuthenticated && !isInFaultView && !isInFaultList;

    const canDownload = useSelector((state) => state.permissions.canDownload);
    const userLanguage = useSelector((state) => state.permissions.language);
    const { isLoaded: isDownloadedLoaded, hasMultipleSections, sectionGroupKeys, sectionGroups, hasCover } = useDownloadMetadata(canDownload ? partNumber : null);
    const showDownloadMenuItem = phoneMode && isDownloadedLoaded && isAuthenticated && isInDocumentView;

    if ((!showFeedbackMenuItem && !showDownloadMenuItem) || isInDocumentListView || isInEngineInfo) {
        return null;
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setShowDownloadSubMenu(false);
        setAnchorEl(null);
    };

    const handleFullDownload = () => {
        if (drawerOpen) {
            return;
        }

        //setMenuOpen(false);
        handleClose();
        setDownloadFinished(false);
        setDrawerOpen(true);
        getAuthenticatedHeaders().then((headers) => {
            return fetch(`/document/pdf/${partNumber}.pdf`, { headers })
                .then(response => {
                    return new Promise(function (resolve, reject) {
                        if (response.status >= 400) {
                            reject(response);
                            return
                        }

                        resolve(response);
                        return;
                    });
                })
                .then(response => response.blob())
                .then(blob => saveAs(blob, `${partNumber}.pdf`))
                .then(() => logDocumentDownload(partNumber, userLanguage), handleFullDownloadError)
                .finally(() => setDownloadFinished(true));
        });
    }


    const handleSectionDownload = (sectionFileName, startPage, endPage, displayName) => {
        if (drawerOpen) {
            return;
        }

        let uri = `/document/pdf/${partNumber}/section/${sectionFileName}/watermark`;
        if (!!startPage && !!endPage) {
            uri += `?start=${startPage}&end=${endPage}`;
        }

        //setMenuOpen(false);
        handleClose();
        setDownloadFinished(false);
        setDrawerOpen(true);
        getAuthenticatedHeaders().then((headers) => {
            return fetch(uri, { headers })
                .then(response => {
                    return new Promise(function (resolve, reject) {
                        if (response.status >= 400) {
                            reject(response);
                            return
                        }

                        resolve(response);
                        return;
                    });
                })
                .then(response => response.blob())
                .then(blob => saveAs(blob, `${partNumber}_${sectionFileName.replace('.pdf', '')}.pdf`))
                .then(() => logDocumentDownload(partNumber, userLanguage, displayName), handleFullDownloadError)
                .finally(() => setDownloadFinished(true));
        });
    }

    const handleFullDownloadError = (error) => {
        console.log('error downloading', error);
        logDownloadFailed(partNumber);
        showErrorModal('An error occurred generating a file for download.')
    }

    return (
        <>
            <FeedbackModal
                open={showFeedback}
                onClose={() => setShowFeedback(false)}
            />
            {drawerOpen ? (
                <MessageContainer
                    offsetLeft={anchorRef?.current?.offsetLeft - 200}
                    offsetTop={anchorRef?.current?.offsetTop * 2.5}
                    finished={downloadFinished}
                >
                    {!downloadFinished ? (
                        <>
                            <div style={{ padding: '0.3em 0.5em', float: 'left' }}>
                                Generating PDF
                            </div>
                            <div style={{ padding: '0.3em 0.5em', float: 'left' }}><Spinner displayInline width={20} /></div>
                            <div style={{ clear: 'both' }}></div>
                        </>
                    ) : null}
                    <div style={{ fontSize: '0.9em' }}>
                        {!downloadFinished ?
                            'This feature will download a PDF File to your downloads folder that can be opened in a PDF Reader application when offline.' :
                            'A PDF File was downloaded to your downloads folder that can be opened in a PDF Reader application when offline.'}
                        <br />
                        {downloadFinished ? (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant='contained'
                                    style={{ minWidth: 'unset', margin: '0.5em' }}
                                    onClick={() => {
                                        setDrawerOpen(false);
                                        setDownloadFinished(false);
                                    }}
                                >
                                    Ok
                                </Button>
                            </div>
                        ) : null}
                    </div>
                </MessageContainer>
            ) : null}
            <Button
                id="app-menu-button"
                aria-controls={open ? 'app-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant='outline'
                style={{ marginLeft: !spMode ? '-16px' : '40px' }}
                ref={anchorRef}
            >
                <MenuIcon />
            </Button>
            <Menu
                id="app-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'app-menu-button',
                    },
                }}
            >
                {showFeedbackMenuItem && !showDownloadSubMenu ? (
                    <MenuItem
                        onClick={() => {
                            setShowFeedback(!showFeedback);
                            handleClose();
                        }}
                        style={{ margin: 8 }}
                        selected={location.pathname.indexOf('/literature') >= 0 && location.pathname.indexOf('/fault') < 0}
                    >
                        <ListItemIcon><ChatIcon /></ListItemIcon>
                        <ListItemText>Feedback</ListItemText>
                    </MenuItem>
                ) : null}
                {showDownloadMenuItem && !showDownloadSubMenu ? (
                    <MenuItem
                        onClick={() => {
                            if (hasMultipleSections) {
                                setShowDownloadSubMenu(!showDownloadSubMenu);
                            } else {
                                handleFullDownload();
                            }
                        }}
                        style={{ margin: 8 }}
                        selected={location.pathname.indexOf('/fault') >= 0}
                    >
                        <ListItemIcon><FileDownloadIcon /></ListItemIcon>
                        <ListItemText>Download</ListItemText>
                    </MenuItem>
                ) : null}
                {showDownloadSubMenu ? (
                    <>
                        <MenuItem
                            onClick={() => handleFullDownload()}>
                            Full Document
                        </MenuItem>
                        <Divider />
                        {sectionGroupKeys.map((key) => {
                            const subSections = sectionGroups[key];

                            return (
                                <DownloadSectionMenuItem
                                    key={key}
                                    partNumber={partNumber}
                                    hasCover={hasCover}
                                    item={key}
                                    subSections={subSections}
                                    onDownload={handleSectionDownload}
                                />
                            );
                        })}
                    </>
                ) : null}
            </Menu>
        </>
    );
}